import React from 'react';

export interface StaleDataBannerProps {
  /** When the currently-displayed data was last successfully fetched. */
  dataUpdatedAt: number;
  /** Whether the most recent background refetch attempt failed. */
  isError: boolean;
  /** Manually re-trigger a fetch. */
  onRetry?: () => void;
  /** Data older than this (ms) is considered stale even without a fetch error. Default 5 minutes. */
  staleAfterMs?: number;
}

function formatAge(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'less than a minute ago';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return '1 hour ago';
  return `${hours} hours ago`;
}

/**
 * Warns the user when the data on screen is not fresh - either because the last
 * background refresh failed (showing cached/last-known-good data) or because it
 * simply hasn't been refetched in a while. Purely derived from React Query state,
 * no backend changes required.
 */
export const StaleDataBanner: React.FC<StaleDataBannerProps> = ({
  dataUpdatedAt,
  isError,
  onRetry,
  staleAfterMs = 5 * 60 * 1000,
}) => {
  if (!dataUpdatedAt) return null;

  const age = Date.now() - dataUpdatedAt;
  const isStale = isError || age > staleAfterMs;

  if (!isStale) return null;

  return (
    <div
      role="status"
      className="mb-4 flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
    >
      <div className="flex items-center gap-2">
        <svg className="h-4 w-4 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <span>
          {isError
            ? `Showing last-known data from ${formatAge(age)} — the latest refresh failed.`
            : `Showing cached data from ${formatAge(age)}.`}
        </span>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="flex-none rounded-md border border-amber-400 bg-card px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-600 dark:bg-transparent dark:text-amber-200 dark:hover:bg-amber-900/50"
        >
          Retry now
        </button>
      )}
    </div>
  );
};
